// ═══════════════════════════════════════════════════════════════════
// TMS GAMMA — App Principal · Layout com Header + Sidebar + Footer
// ═══════════════════════════════════════════════════════════════════
import React, { useState, useMemo, lazy, Suspense } from "react";
import { supabase }        from "./lib/supabaseClient";
import { useSupabaseData } from "./utils/useSupabaseData";
import Login               from "./modules/Login";
import { GLOBAL_CSS, T, syne, mono, expandida, aplicarTema } from "./theme/tokens";
import { FILIAL_CFG, filtrarPorFilial, corFilial, filiaisDoUsuario } from "./constants/filiais";
import { validarNFCache, criarClientesMap, criarMunicipiosMap, tabelasParaCotar } from "./utils/negocio";
import { definirTransportadoras } from "./utils/transpLookup";
import { precarregarXLSX } from "./utils/planilhaXLSX";

import Sidebar, { TELAS, telasDe, secaoDe } from "./components/layout/Sidebar";
import { Icone, ICONES } from "./components/ui/Icone";
import { PainelAlertas }from "./components/ui/PainelAlertas";
import { SkeletonPagina, Girando } from "./components/ui/Skeleton";
import { useAlertas }   from "./utils/useAlertas";
import { useUsuarioAtual } from "./utils/useUsuarioAtual";
import { podeEditarEmailTransportadora } from "./utils/cargos";
import { useLubpressData } from "./utils/useLubpressData";
import { useNavegacao, grupoDaUrl } from "./utils/useNavegacao";

// ═══ Carregamento sob demanda ═══════════════════════════════════════
// Os 42 modulos eram import estatico: abrir o Dashboard baixava tambem o
// codigo da Auditoria, do Mapa e de todos os outros — 481 kB gzip num
// bundle unico. Com lazy() cada tela vira um chunk separado, buscado na
// primeira vez que o usuario abre aquela aba.
//
// Login e Sidebar ficam de fora de proposito: aparecem sempre, e adiar o
// que sempre aparece so troca peso por piscada.
// ═══════════════════════════════════════════════════════════════════
const Analise = lazy(() => import("./modules/Analise"));
const AuditoriaCTe = lazy(() => import("./modules/AuditoriaCTe"));
const AuditoriaFoto = lazy(() => import("./modules/AuditoriaFoto"));
const Clientes = lazy(() => import("./modules/Clientes"));
const Comparativo = lazy(() => import("./modules/Comparativo"));
const ComparativoMun = lazy(() => import("./modules/ComparativoMunicipio"));
const Conferencia = lazy(() => import("./modules/Conferencia"));
const ConsultaEntrega = lazy(() => import("./modules/ConsultaEntrega"));
const Cotacao = lazy(() => import("./modules/Cotacao"));
const CteSefaz = lazy(() => import("./modules/CteSefaz"));
const Dashboard = lazy(() => import("./modules/Dashboard"));
const Divergencias = lazy(() => import("./modules/Divergencias"));
const Feriados = lazy(() => import("./modules/Feriados"));
const GammaBot = lazy(() => import("./modules/GammaBot"));
const LubClientes = lazy(() => import("./modules/LubClientes").then(m => ({ default: m.LubClientes })));
const LubComparativo = lazy(() => import("./modules/LubComparativo").then(m => ({ default: m.LubComparativo })));
const LubComparativoMun = lazy(() => import("./modules/LubComparativoMun").then(m => ({ default: m.LubComparativoMun })));
const LubConferencia = lazy(() => import("./modules/LubConferencia").then(m => ({ default: m.LubConferencia })));
const LubCusto = lazy(() => import("./modules/LubCusto").then(m => ({ default: m.LubCusto })));
const LubDashboard = lazy(() => import("./modules/LubDashboard").then(m => ({ default: m.LubDashboard })));
const LubGranel = lazy(() => import("./modules/LubGranel").then(m => ({ default: m.LubGranel })));
const LubMapa = lazy(() => import("./modules/LubMapa").then(m => ({ default: m.LubMapa })));
const LubMargem = lazy(() => import("./modules/LubMargem").then(m => ({ default: m.LubMargem })));
const LubMunicipios = lazy(() => import("./modules/LubMunicipios").then(m => ({ default: m.LubMunicipios })));
const LubNotas = lazy(() => import("./modules/LubNotas").then(m => ({ default: m.LubNotas })));
const LubOperacao = lazy(() => import("./modules/LubOperacao").then(m => ({ default: m.LubOperacao })));
const LubOportunidades = lazy(() => import("./modules/LubOportunidades").then(m => ({ default: m.LubOportunidades })));
const LubOtif = lazy(() => import("./modules/LubOtif").then(m => ({ default: m.LubOtif })));
const LubProdutos = lazy(() => import("./modules/LubProdutos").then(m => ({ default: m.LubProdutos })));
const LubRetornoVibra = lazy(() => import("./modules/LubRetornoVibra").then(m => ({ default: m.LubRetornoVibra })));
const LubRotasVibra = lazy(() => import("./modules/LubRotasVibra").then(m => ({ default: m.LubRotasVibra })));
const LubTabelas = lazy(() => import("./modules/LubTabelas").then(m => ({ default: m.LubTabelas })));
const LubTabelasVibra = lazy(() => import("./modules/LubTabelasVibra").then(m => ({ default: m.LubTabelasVibra })));
const LubTransportadoras = lazy(() => import("./modules/LubTransportadoras").then(m => ({ default: m.LubTransportadoras })));
const MapaOperacional = lazy(() => import("./modules/MapaOperacional"));
const Municipios = lazy(() => import("./modules/Municipios"));
const NotasFiscais = lazy(() => import("./modules/NotasFiscais"));
const Ocorrencias = lazy(() => import("./modules/Ocorrencias"));
const Oportunidades = lazy(() => import("./modules/Oportunidades"));
const RelatorioMensal = lazy(() => import("./modules/RelatorioMensal"));
const RevisaoPrazos = lazy(() => import("./modules/RevisaoPrazos"));
const Tabelas = lazy(() => import("./modules/Tabelas"));
const TransportadorasM = lazy(() => import("./modules/Transportadoras"));
const Usuarios = lazy(() => import("./modules/Usuarios"));
const Inicio = lazy(() => import("./modules/Inicio"));


// ── ErrorBoundary para capturar erros em produção ────────────
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { erro: null }; }
  static getDerivedStateFromError(e) { return { erro: e.message }; }
  render() {
    // Pelo tema, com reserva: as cores eram fixas de um tema antigo (#301).
    // O "||" cobre um erro antes de aplicarTema, quando T ainda está vazio.
    if (this.state.erro) return (
      <div role="alert" style={{ minHeight:"100vh", display:"flex", alignItems:"center",
        justifyContent:"center", background:T.bg || "#0f0f11", flexDirection:"column",
        gap:12, padding:20, textAlign:"center" }}>
        <Icone d={ICONES.alerta} size={34} color={T.red || "#E82234"} strokeWidth={1.8}/>
        <div style={{ color:T.text || "#f4f4f1", fontSize:20, ...expandida }}>
          Erro ao carregar o sistema
        </div>
        <div style={{ color:T.sub || "#b0b0b7", fontSize:13, maxWidth:420, lineHeight:1.5 }}>
          Recarregue a página. Se o erro voltar, mande a mensagem abaixo para quem
          cuida do sistema.
        </div>
        <div style={{ color:T.red || "#E82234", fontSize:12.5, ...mono,
          background:T.card || "#1b1b1f", border:`1px solid ${T.border || "#2a2a30"}`,
          padding:"8px 16px", borderRadius:8, maxWidth:520 }}>
          {this.state.erro}
        </div>
        <button type="button" onClick={() => window.location.reload()}
          style={{ marginTop:8, padding:"9px 20px", borderRadius:8, border:"none",
            background:T.red || "#E82234", color:"#fff", cursor:"pointer", fontWeight:700,
            fontSize:13, display:"flex", alignItems:"center", gap:7, ...syne }}>
          <Icone d={ICONES.refazer} size={15} strokeWidth={2}/>
          Recarregar
        </button>
      </div>
    );
    return this.props.children;
  }
}

function useTelaEstreita(px) {
  const consulta = `(max-width: ${px}px)`;
  const [v, setV] = useState(() => window.matchMedia?.(consulta).matches ?? false);
  React.useEffect(() => {
    const m = window.matchMedia?.(consulta);
    if (!m) return undefined;
    const f = (e) => setV(e.matches);
    m.addEventListener("change", f);
    return () => m.removeEventListener("change", f);
  }, [consulta]);
  return v;
}

export default function App() {
  // "gamma" | "lubpress". Começa pelo grupo da tela na URL: era null, e
  // todo F5 voltava ao seletor mesmo com /notas na barra (#232).
  const [grupo,       setGrupo]       = useState(() => grupoDaUrl(TELAS));
  // Tela aberta na URL e saída com trabalho não salvo (#214, #184). Só as
  // telas do cargo valem (#315), e só depois de o perfil chegar: antes dele
  // o cargo é o padrão, e um F5 em /dashboard trocaria a URL pela Início.
  const usuario = useUsuarioAtual();
  const { cargo: cargoUsuario, filiais: filiaisUsuario } = usuario;
  const telas = useMemo(() => grupo && usuario.id ? telasDe(grupo, { cargo: cargoUsuario, filiais: filiaisUsuario }) : undefined,
    [grupo, usuario.id, cargoUsuario, filiaisUsuario]);
  const { page, setPage, sair, guardaSaida } = useNavegacao(telas);
  const [filial, setFilial] = useState("todos");
  // ── Tema dark/light ──────────────────────────────────────────
  const [tema, setTema] = useState(() => localStorage.getItem("tms_tema") || "dark");
  // No render, não no efeito: T é lido pelos filhos deste mesmo render, e
  // o efeito só rodaria depois deles — a troca de tema sairia atrasada (#216).
  aplicarTema(tema);
  React.useEffect(() => {
    document.documentElement.setAttribute("data-theme", tema);
    localStorage.setItem("tms_tema", tema);
  }, [tema]);
  function toggleTema() { setTema(t => t === "dark" ? "light" : "dark"); }

  // ── Tela estreita (celular, notebook pequeno) ────────────────
  // Abaixo de 900px o menu vira gaveta e o cabeçalho encolhe (#305). Com o
  // menu fixo, a 390px sobravam 150px para o conteúdo.
  const estreito = useTelaEstreita(900);
  const [menuAberto, setMenuAberto] = useState(false);
  React.useEffect(() => { if (!estreito) setMenuAberto(false); }, [estreito]);

  // ── Autenticação ─────────────────────────────────────────────
  const [user,        setUser]        = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  React.useEffect(() => {
    // Libera o app do "Carregando..." de forma robusta. O getSession() do
    // Supabase v2 usa lock do navegador (navigator.locks) e, no PRIMEIRO
    // carregamento, às vezes trava/demora — sem catch nem timeout o app ficava
    // preso e só destravava no F5 (sessão já em memória).
    let resolvido = false;
    const concluir = (session) => {
      if (resolvido) return;
      resolvido = true;
      setUser(session?.user || null);
      setAuthChecked(true);
    };

    supabase.auth.getSession()
      .then(({ data:{ session } }) => concluir(session))
      .catch((e) => { console.error("getSession falhou:", e); concluir(null); });

    // onAuthStateChange dispara INITIAL_SESSION logo após inscrever — destrava
    // mesmo se o getSession travar no lock do token.
    const { data:{ subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setUser(session?.user || null);
      if (!session) setGrupo(null); // ao sair, volta para o seletor de grupo
      concluir(session);
    });

    // Rede de segurança: nunca ficar preso em "Carregando..." além de 4s.
    const timeout = setTimeout(() => concluir(null), 4000);

    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, []);

  // ── Datasets via Supabase (só carrega após login) ────────────
  // Hook Lubpress — só ativa quando grupo="lubpress", não carrega dados Gamma
  const lubpressData = useLubpressData(!!user && grupo === "lubpress");

  const {
    notas, setNotas, atualizarNotasParcial, auditoriaFretes, salvarAuditoriaFretes, kpis, clientes, setClientes,
    atualizarClientesParcial, excluirCliente,
    tabelas, setTabelas, deletarTabela, municipios, setMunicipios,
    ocorrencias, setOcorrencias, atualizarOcorrenciasParcial, excluirOcorrencia,
    transportadoras, setTransportadoras, deletarTransportadora,
    feriados, setFeriados, excluirFeriado,
    auditorias, salvarAuditoria, excluirAuditoria, rastreioSsw, recarregarRastreioSsw,
    loading, loadingNotas, loadingClientes, progresso, erro: erroDados, erroSalvar, salvando,
    recarregarNotas, garantirNotas, carregarPeriodo, notasDesde, garantirAuditoriaFretes, recarregarKpis, excluirNotas,
  } = useSupabaseData(!!user && grupo === "gamma");

  // ── Notas fiscais na entrada (#308) ──────────────────────────────
  // Antes, só as telas de uma lista (PAGINAS_COM_NOTAS) pediam as notas, e
  // elas chegavam em lotes com o sistema já aberto: números se completando
  // aos poucos, e tela fora da lista sem nota nenhuma. Agora o grupo Gamma
  // busca as notas logo na entrada, e o carregamento inicial espera por
  // elas. O custo é a abertura mais lenta (~13 s com 99.713 notas, contra
  // ~5 s); baixar menos na abertura é a #179.
  React.useEffect(() => {
    if (grupo !== "gamma" || !user || !garantirNotas) return;
    garantirNotas();
  }, [grupo, user, garantirNotas]);
  React.useEffect(() => {
    if (grupo === "gamma" && page === "mapaOp") garantirAuditoriaFretes();
  }, [page, grupo, garantirAuditoriaFretes]);

  // ── Tela de loading inicial (Gamma e Lubpress) ────────────────
  // Espera importar TUDO antes de abrir o sistema — inclusive notas e clientes,
  // que carregam paginados. Antes só esperava a fase crítica (tabelas+municípios)
  // e a dashboard abria com zeros que iam preenchendo ao vivo.
  // As notas NÃO entram no gate: elas carregam sob demanda. Os KPIs do
  // Dashboard vêm agregados do banco, então a abertura não espera por elas.
  const carregandoDados = grupo === "lubpress"
    ? lubpressData.loading
    : (loading || loadingClientes || loadingNotas);
  const [loadDemorado,  setLoadDemorado]  = useState(false);
  const [forcarEntrada, setForcarEntrada] = useState(false);
  // "Demorou" só dispara se o progresso ficar PARADO por 20s (rede travada).
  // Enquanto notas/clientes vão chegando (progresso muda), o timer reinicia.
  React.useEffect(() => {
    if (!carregandoDados) { setLoadDemorado(false); return; }
    setLoadDemorado(false);
    const t = setTimeout(() => setLoadDemorado(true), 20000);
    return () => clearTimeout(t);
  }, [carregandoDados, progresso]);

  const loadingCli = false;  // clientes carregam em background
  const loadingNF  = false;  // notas carregam em background
  // Entrega ao negocio.js a lista vinda do Supabase. Ele deixou de importar
  // constants/transportadoras estaticamente porque isso colocava as 52
  // transportadoras reais no bundle inicial, baixavel sem login (#32).
  //
  // useMemo e nao useEffect de proposito: useEffect roda DEPOIS do render, e
  // os filhos ja teriam validado a primeira leva de notas sem a lista —
  // mostrando codigo no lugar do nome ate o segundo render.
  useMemo(() => definirTransportadoras(transportadoras), [transportadoras]);

  // A biblioteca de planilha deixou de vir de <script> do CDN (#20) e agora
  // e chunk sob demanda. Dispara aqui, ja autenticado, para o primeiro
  // clique em exportar nao pagar o download inteiro.
  useMemo(() => { if (user) precarregarXLSX(); }, [user]);

  const { nome, cargoLabel, iniciais } = usuario;

  // ── Índices O(1) ──────────────────────────────────────────────
  const tabelasCotar  = useMemo(() => tabelasParaCotar(tabelas, transportadoras), [tabelas, transportadoras]);
  const clientesMap   = useMemo(() => criarClientesMap(clientes),    [clientes]);
  const municipiosMap = useMemo(() => criarMunicipiosMap(municipios), [municipios]);

  // ── Alertas internos ──────────────────────────────────────────
  const alertas = useAlertas({ notas, tabelas, ocorrencias, clientesMap, municipiosMap, auditorias, rastreioSsw, usuario, kpis });

    // ── Filtro por filial ─────────────────────────────────────────
  // Com filiais de acesso (#315), "todos" quer dizer as do usuário.
  const minhasFiliais = useMemo(() => filiaisDoUsuario({ cargo: cargoUsuario, filiais: filiaisUsuario }),
    [cargoUsuario, filiaisUsuario]);
  const filtroFilial = filial === "todos" && minhasFiliais ? minhasFiliais : filial;
  const dadosFilial = useMemo(() => filtrarPorFilial(filtroFilial, {
    municipios, ocorrencias, tabelas, notas,
  }), [filtroFilial, municipios, ocorrencias, tabelas, notas]);

  // ── NFs incorretas ───────────────────────────────────────────
  const nfIncorr = useMemo(() => {
    const t0 = performance.now();
    const r = dadosFilial.notas.reduce((acc, n) => {
      try {
        const v = validarNFCache(n, clientesMap, municipiosMap);
        return acc + (v.status === "incorreto" ? 1 : 0);
      } catch { return acc; }
    }, 0);
    if (dadosFilial.notas.length > 1000)
      console.log(`⏱ [validação] ${dadosFilial.notas.length} notas: ${Math.round(performance.now()-t0)}ms`);
    return r;
  }, [dadosFilial.notas, clientesMap, municipiosMap]);

  const filialLabel = filial === "todos" && minhasFiliais ? minhasFiliais.join(", ")
    : (FILIAL_CFG[filial] ?? {}).label ?? filial;
  const filialCor   = corFilial(filial);

  // ── Auth check ────────────────────────────────────────────────
  if (!authChecked) return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center",
      justifyContent:"center", background:T.bg }}>
      <div style={{ color:T.sub, fontSize:13 }}>Carregando...</div>
    </div>
  );

  // Sem login: mostra tela de login
  if (!user) return <Login onLogin={(u, g) => { setUser(u); setGrupo(g); }}/>;
  // Com login mas sem grupo: mostra o seletor de grupo novamente
  // (segurança: não entra no sistema sem escolher o contexto). O contexto
  // escolhido vale também quando vem da URL de uma tela (#232).
  if (!grupo) return <Login onLogin={(u, g) => { setUser(u); setGrupo(g); }}/>;

  // ── Loading inicial (importação dos dados do Supabase) ────────
  // Vale para Gamma (tabelas+municípios) e Lubpress. Com timeout: se demorar
  // demais, oferece recarregar ou continuar mesmo assim.
  if (carregandoDados && !forcarEntrada) {
    return (
      <div style={{ display:"flex", alignItems:"center", justifyContent:"center",
        height:"100vh", background:T.bg, flexDirection:"column", gap:14, padding:20 }}>
        <style>{GLOBAL_CSS}</style>
        <img src="/logo.png" alt="Gamma TMS"
          style={{ width:48, height:48, borderRadius:10, objectFit:"contain" }}/>
        <div style={{ color:T.text, fontSize:20, ...expandida }}>
          Preparando {grupo === "lubpress" ? "a Lubpress" : "o Gamma TMS"}
        </div>
        {/* O que está vindo e o que já chegou. Antes era "Carregando..." e
            uma linha de progresso solta, sem dizer quanto faltava (#301). */}
        <ul aria-live="polite" style={{ listStyle:"none", margin:0, padding:"12px 16px",
          minWidth:300, background:T.card, border:`1px solid ${T.border}`, borderRadius:10,
          display:"grid", gap:10 }}>
          {(grupo === "lubpress"
            ? [{ rotulo:"Dados da operação Vibra", estado:"vindo" }]
            : [
                { rotulo:"Tabelas, municípios e transportadoras", estado: loading ? "vindo" : "pronto" },
                { rotulo:"Clientes", estado: loading ? "espera" : loadingClientes ? "vindo" : "pronto",
                  detalhe: progresso.startsWith("Clientes") ? progresso.replace(/^Clientes:\s*/, "").replace(/\.\.\.$/, "") : "" },
                // Notas vêm junto desde a #308; o número sobe a cada lote de 20 mil.
                { rotulo:"Notas fiscais", estado: loadingNotas ? "vindo" : "pronto",
                  detalhe: progresso.startsWith("Notas") ? progresso.replace(/^Notas:\s*/, "").replace(/\.\.\.$/, "") : "" },
              ]
          ).map(e => (
            <li key={e.rotulo} style={{ display:"flex", alignItems:"center", gap:10, fontSize:13,
              color: e.estado === "espera" ? T.muted : T.text }}>
              {e.estado === "pronto"
                ? <Icone d={ICONES.confirmar} size={16} color={T.green} strokeWidth={2.2}/>
                : e.estado === "vindo"
                  ? <Girando size={16}/>
                  : <span style={{ width:16, height:16, borderRadius:"50%", border:`2px solid ${T.border2}` }}/>}
              <span style={{ flex:1 }}>{e.rotulo}</span>
              {e.detalhe && <span style={{ color:T.sub, fontSize:12.5, ...mono }}>{e.detalhe}</span>}
            </li>
          ))}
        </ul>
        {loadDemorado && (
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:10, marginTop:6 }}>
            <div style={{ color:T.sub, fontSize:13, maxWidth:340, textAlign:"center", lineHeight:1.5 }}>
              Está demorando mais que o normal para importar os dados do Supabase.
            </div>
            <div style={{ display:"flex", gap:10 }}>
              <button type="button" onClick={() => window.location.reload()}
                style={{ padding:"8px 16px", borderRadius:8, border:"none", cursor:"pointer",
                  background:T.red, color:"#fff", fontSize:13, fontWeight:700 }}>
                Recarregar
              </button>
              <button type="button" onClick={() => setForcarEntrada(true)}
                style={{ padding:"8px 16px", borderRadius:8, cursor:"pointer",
                  background:"transparent", color:T.sub, fontSize:13, fontWeight:600,
                  border:`1px solid ${T.border2}` }}>
                Continuar mesmo assim
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <ErrorBoundary>
    <>
      <style>{GLOBAL_CSS}</style>

      {/* ══ LAYOUT: Sidebar full-height | Coluna direita ══════════ */}
      <div style={{ display:"flex", height:"100vh", width:"100vw" }}>

        {/* Sidebar ocupa toda a altura; em tela estreita vira gaveta, com
            fundo escurecido que fecha ao toque (#305). */}
        {estreito && menuAberto && (
          <div aria-hidden="true" onClick={() => setMenuAberto(false)}
            style={{ position:"fixed", inset:0, zIndex:290, background:"rgba(0,0,0,.5)" }}/>
        )}
        <Sidebar
          gaveta={estreito} aberta={menuAberto} onFechar={() => setMenuAberto(false)}
          active={page} onNav={(id) => { setMenuAberto(false); return setPage(id); }}
          filial={filial} onFilial={f => sair(() => setFilial(f))}
          ocorrencias={dadosFilial.ocorrencias}
          nfIncorr={nfIncorr}
          grupo={grupo} usuario={usuario}
          onTrocarGrupo={() => sair(() => setGrupo(null))}
        />

        {/* Coluna direita: header + conteúdo + footer */}
        <div style={{ flex:1, display:"flex", flexDirection:"column", height:"100vh", minWidth:0 }}>

          {/* ══ HEADER ════════════════════════════════════════════ */}
          <header style={{
            height:56, flexShrink:0,
            background:T.header,
            borderBottom:`1px solid ${T.border}`,
            display:"flex", alignItems:"center",
            justifyContent:"space-between",
            padding: estreito ? "0 12px" : "0 24px", gap:10,
            position:"sticky", top:0, zIndex:100,
          }}>
            {/* Sem o nome da tela: ela já está no título logo abaixo, com o
                sobretítulo da seção (#299). Ficam grupo e filial. */}
            <div style={{ display:"flex", alignItems:"center", gap:8, minWidth:0 }}>
              {estreito && (
                <button type="button" onClick={() => setMenuAberto(true)}
                  aria-label="Abrir menu" aria-expanded={menuAberto}
                  style={{ background:"none", border:`1px solid ${T.border}`, borderRadius:8,
                    width:36, height:36, display:"inline-flex", alignItems:"center",
                    justifyContent:"center", color:T.text, cursor:"pointer", flexShrink:0 }}>
                  <Icone d={["M3 6h18", "M3 12h18", "M3 18h18"]} size={18}/>
                </button>
              )}
              {!estreito && <span style={{ fontSize:13, color:T.sub, ...syne }}>Gestão de Frete</span>}
              {grupo === "lubpress" && (
                <span style={{ fontSize:11.5, fontWeight:700, color:T.purple,
                  background:T.purple + "14", border:`1px solid ${T.purple}30`,
                  padding:"2px 9px", borderRadius:99, marginLeft:4, whiteSpace:"nowrap", ...syne }}>
                  Lubpress
                </span>
              )}
              {filial !== "todos" && (
                <span style={{
                  fontSize:10, fontWeight:700,
                  color:filialCor, background:filialCor+"14",
                  border:`1px solid ${filialCor}30`,
                  padding:"2px 9px", borderRadius:99, marginLeft:4, ...syne,
                }}>{filialLabel}</span>
              )}
            </div>

            {/* Ações e usuário. Ordem: avisos de estado, sino, tema | usuário, Sair. */}
            <div style={{ display:"flex", alignItems:"center", gap: estreito ? 8 : 12 }}>
              {/* Indicador de salvando */}
              {salvando && !erroSalvar && (
                <div style={{ fontSize:11.5, color:T.muted, whiteSpace:"nowrap",
                  display:"flex", alignItems:"center", gap:6 }}>
                  <Girando size={12}/> Salvando...
                </div>
              )}
              {/* Erro ao salvar — visível e persistente, com botão fechar */}
              {erroSalvar && (
                <div role="alert" style={{ fontSize:11.5, color:"#fff", background:T.red,
                  padding:"4px 10px", borderRadius:6, fontWeight:700, whiteSpace:"nowrap",
                  display:"flex", alignItems:"center", gap:8 }}>
                  <Icone d={ICONES.alerta} size={14} strokeWidth={2}/>
                  {erroSalvar}
                  <button type="button" onClick={() => window.location.reload()}
                    style={{ background:"rgba(255,255,255,.2)", border:"none", color:"#fff",
                      borderRadius:4, padding:"2px 8px", fontSize:11, cursor:"pointer" }}>
                    Recarregar
                  </button>
                </div>
              )}
              {/* Progresso de carregamento em background */}
              {progresso && (
                <div style={{ fontSize:11.5, color:T.muted, ...mono, whiteSpace:"nowrap",
                  display:"flex", alignItems:"center", gap:6 }}>
                  <Girando size={12}/> {progresso}
                </div>
              )}
              {/* Sino de alertas — inline no header */}
              <PainelAlertas alertas={alertas} onNav={setPage}/>

              {/* Tema: só o ícone do modo para o qual a tela vai mudar */}
              <button type="button" onClick={toggleTema}
                title={tema === "dark" ? "Mudar para modo dia" : "Mudar para modo noturno"}
                aria-label={tema === "dark" ? "Mudar para modo dia" : "Mudar para modo noturno"}
                style={{
                  width:34, height:34, borderRadius:"50%", flexShrink:0,
                  display:"inline-flex", alignItems:"center", justifyContent:"center",
                  background:"none", border:`1px solid ${T.border}`, cursor:"pointer",
                  color:T.muted, transition:"var(--m-cor)",
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = T.red; e.currentTarget.style.color = T.text; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.muted; }}>
                {tema === "dark" ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                  </svg>
                )}
              </button>

              <div aria-hidden="true" style={{ width:1, height:22, background:T.border, flexShrink:0 }}/>

              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <div style={{
                  width:32, height:32, borderRadius:"50%", background:T.red, flexShrink:0,
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:12, fontWeight:800, color:"#fff",
                }}>{iniciais}</div>
                {!estreito && (
                  <div style={{ lineHeight:1.25 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:T.text, ...syne }}>{nome}</div>
                    {/* Dizia "· Gamma" também na Lubpress (#305). */}
                    <div style={{ fontSize:11, color:T.muted }}>
                      {cargoLabel} · {grupo === "lubpress" ? "Lubpress" : "Gamma"}
                    </div>
                  </div>
                )}
              </div>

              <button type="button" onClick={() => sair(() => supabase.auth.signOut())}
                style={{ height:32, padding:"0 12px", borderRadius:8, fontSize:11, fontWeight:600,
                  border:`1px solid ${T.border}`, background:"none",
                  color:T.muted, cursor:"pointer", transition:"var(--m-cor)", ...syne }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = T.red; e.currentTarget.style.color = T.text; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.muted; }}>
                Sair
              </button>
            </div>
          </header>

          {/* Conteúdo principal */}
          <main style={{
            flex:1, overflowY:"auto",
            padding: estreito ? "16px 14px" : "24px 28px",
            background:T.bg,
            minHeight:0, minWidth:0,
          }}>
            {/* Fronteira de carregamento dos modulos sob demanda. O fallback
                nao anima entrada: varios modulos ja trazem .fu na raiz, e as
                duas entradas somadas ficam visiveis — exatamente o que a
                Golden Rule manda evitar. */}
            <Suspense fallback={<SkeletonPagina />}>
            {/* Sobretítulo da seção acima do título de cada tela, como no
                Risso Hub (#299). Vem daqui para valer em todas as telas sem
                mexer em cada uma; a Início não tem seção. */}
            {secaoDe(page) && (
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:6,
                fontSize:11, fontWeight:700, letterSpacing:".14em", textTransform:"uppercase",
                color: grupo === "lubpress" ? T.purple : T.red, ...syne }}>
                <span aria-hidden="true" style={{ width:18, height:2, borderRadius:1,
                  background: grupo === "lubpress" ? T.purple : T.red }}/>
                {secaoDe(page)}
              </div>
            )}
            {(page === "inicio" || page === "lubInicio") && (
              <Inicio grupo={grupo} nome={nome} usuario={usuario} alertas={alertas} onNav={setPage}
                carregando={loadingNotas}/>
            )}
            {page === "dashboard" && (
              <Dashboard
                filialSel={filial} filialLabel={filialLabel}
                tabelas={dadosFilial.tabelas} clientes={clientes}
                municipios={dadosFilial.municipios} ocorrencias={dadosFilial.ocorrencias}
                notas={dadosFilial.notas} onNav={setPage}
                clientesMap={clientesMap} municipiosMap={municipiosMap}
                transportadoras={transportadoras} auditorias={auditorias}
                kpis={kpis}
              />
            )}
            {page === "notas" && (
              <NotasFiscais
                notas={dadosFilial.notas} setNotas={setNotas}
                atualizarNotasParcial={atualizarNotasParcial}
                clientes={clientes} municipios={municipios}
                tabelas={tabelas}
                transportadoras={transportadoras}
                clientesMap={clientesMap} municipiosMap={municipiosMap}
                rastreioSsw={rastreioSsw} recarregarRastreioSsw={recarregarRastreioSsw}
                nomeUsuario={nome}
                loadingNotas={loadingNotas} progresso={progresso}
                erro={erroDados} recarregarNotas={recarregarNotas} carregarPeriodo={carregarPeriodo} notasDesde={notasDesde}
                recarregarKpis={recarregarKpis} excluirNotas={excluirNotas}
              />
            )}
            {page === "tabelas" && (
              <Tabelas
                tabelas={dadosFilial.tabelas} setTabelas={setTabelas}
                deletarTabela={deletarTabela}
                municipios={dadosFilial.municipios}
                transportadoras={transportadoras}
              />
            )}
            {page === "conferencia" && (
              <Conferencia
                tabelas={dadosFilial.tabelas} municipios={municipios}
                clientes={clientes} filialLabel={filialLabel}
              />
            )}
            {page === "auditoria" && (
              <AuditoriaCTe
                tabelas={dadosFilial.tabelas} clientes={clientes}
                municipios={municipios} filialLabel={filialLabel}
                notas={notas} transportadoras={transportadoras}
                nomeUsuario={nome} usuario={usuario} filialSel={filial}
                salvarAuditoriaFretes={salvarAuditoriaFretes}
                atualizarNotasParcial={atualizarNotasParcial}
                auditorias={auditorias}
                salvarConferencia={salvarAuditoria}
                excluirConferencia={excluirAuditoria}
                guardaSaida={guardaSaida}
              />
            )}
            {page === "divergencias" && <Divergencias origem="gamma" nomeUsuario={nome}/>}
            {page === "cteSefaz" && <CteSefaz grupo="gamma"/>}
            {page === "ocorrencias" && (
              <Ocorrencias
                ocorrencias={dadosFilial.ocorrencias} setOcorrencias={setOcorrencias}
                atualizarOcorrenciasParcial={atualizarOcorrenciasParcial}
                excluirOcorrencia={excluirOcorrencia}
                filialSel={filial} filialLabel={filialLabel}
              />
            )}
            {page === "analise" && (
              <Analise
                tabelas={dadosFilial.tabelas} clientes={clientes}
                notas={dadosFilial.notas} ocorrencias={dadosFilial.ocorrencias}
                municipios={municipios}
                clientesMap={clientesMap} municipiosMap={municipiosMap}
                transportadoras={transportadoras}
                kpis={kpis}
              />
            )}
            {page === "transportadoras" && (
              <TransportadorasM
                transportadoras={transportadoras}
                setTransportadoras={setTransportadoras}
                deletarTransportadora={deletarTransportadora}
              />
            )}
            {page === "clientes" && (
              <Clientes clientes={clientes} setClientes={setClientes} municipios={municipios}
                atualizarClientesParcial={atualizarClientesParcial} excluirCliente={excluirCliente}
                loadingClientes={loadingClientes}/>
            )}
            {page === "municipios" && (
              <Municipios municipios={municipios} setMunicipios={setMunicipios}
                tabelas={tabelas}/>
            )}
            {page === "auditoriaFoto" && (
              <AuditoriaFoto
                notas={notas} tabelas={tabelas} municipios={municipios}
                filialLabel={filialLabel} filialSel={filial} usuario={usuario}
                auditorias={auditorias}
                salvarConferencia={salvarAuditoria}
                excluirConferencia={excluirAuditoria}
              />
            )}
            {page === "consultaEntrega" && <ConsultaEntrega notas={notas} clientes={clientes}/>}
            {page === "cotacao" && (
              <Cotacao tabelas={tabelasCotar} municipios={municipios} clientes={clientes}/>
            )}
            {page === "simulacaoTabela" && (
              <Cotacao simular tabelas={tabelasCotar} municipios={municipios} clientes={clientes}/>
            )}
            {page === "relatorio" && (
              <RelatorioMensal
                notas={notas} tabelas={tabelas} ocorrencias={dadosFilial.ocorrencias}
                transportadoras={transportadoras} clientesMap={clientesMap}
              />
            )}
            {page === "revisaoPrazos" && (
              <RevisaoPrazos
                notas={dadosFilial.notas} tabelas={tabelas}
                clientesMap={clientesMap} transportadoras={transportadoras}
              />
            )}
            {page === "feriados" && (
              <Feriados
                feriados={feriados} setFeriados={setFeriados}
                excluirFeriado={excluirFeriado}
                municipios={municipios} filialLabel={filialLabel}
              />
            )}
            {page === "usuarios" && (
              <Usuarios
                tabelas={tabelas} municipios={municipios}
                ocorrencias={ocorrencias}
              />
            )}
            {page === "mapaOp" && (
              <MapaOperacional
                notas={notas} tabelas={tabelas}
                municipios={municipios} clientes={clientes}
                auditoriaFretes={auditoriaFretes}
              />
            )}
            {page === "comparativo" && (
              <Comparativo
                notas={dadosFilial.notas}
                tabelas={tabelasCotar}
                clientes={clientes}
                municipios={municipios}
              />
            )}
            {page === "compMunicipio" && (
              <ComparativoMun
                notas={notas}
                tabelas={tabelasCotar}
                municipios={municipios}
                clientes={clientes}
              />
            )}
            {page === "oportunidades" && (
              <Oportunidades
                notas={notas}
                clientes={clientes}
                municipios={municipios}
                tabelas={tabelas}
                transportadoras={transportadoras}
              />
            )}

            {/* ── Módulos Lubpress/Vibra ────────────────────────── */}
            {grupo === "lubpress" && (
              <div>
                {page === "lubDashboard" && (
                  <LubDashboard
                    ctes={lubpressData.ctes}
                    transportadoras={lubpressData.transportadoras}
                  />
                )}
                {page === "lubConferencia" && (
                  <LubConferencia
                    tabelasVibra={lubpressData.tabelasVibra}
                    rotasVibra={lubpressData.rotasVibra}
                    ctes={lubpressData.ctes}
                    salvarCtes={lubpressData.salvarCtes}
                    recarregar={lubpressData.recarregar}
                    municipios={lubpressData.municipiosLub}
                    notas={lubpressData.notas}
                  />
                )}
                {page === "lubTabelasVibra" && (
                  <LubTabelasVibra
                    tabelasVibra={lubpressData.tabelasVibra}
                    setVibraTabelas={lubpressData.setVibraTabelas}
                  />
                )}
                {page === "lubCusto" && (
                  <LubCusto
                    ctes={lubpressData.ctes}
                    salvarCtes={lubpressData.salvarCtes}
                    recarregar={lubpressData.recarregar}
                    tabelas={lubpressData.tabelasLub}
                    municipios={lubpressData.municipiosLub}
                    transportadoras={lubpressData.transportadoras}
                  />
                )}
                {page === "lubDivergencias" && <Divergencias origem="lubpress" nomeUsuario={nome}/>}
                {page === "lubCteSefaz" && <CteSefaz grupo="lubpress"/>}
                {page === "lubMargem" && (
                  <LubMargem
                    ctes={lubpressData.ctes}
                    transportadoras={lubpressData.transportadoras}
                  />
                )}
                {page === "lubOtif" && (
                  <LubOtif
                    ctes={lubpressData.ctes}
                    notas={lubpressData.notas}
                    salvarCtes={lubpressData.salvarCtes}
                    recarregar={lubpressData.recarregar}
                    tabelasVibra={lubpressData.tabelasVibra}
                    transportadoras={lubpressData.transportadoras}
                  />
                )}
                {page === "lubGranel" && (
                  <LubGranel ctes={lubpressData.ctes}/>
                )}
                {page === "lubComparativo" && (
                  <LubComparativo
                    tabelasVibra={lubpressData.tabelasVibra}
                    rotasVibra={lubpressData.rotasVibra}
                    tabelas={lubpressData.tabelasLub}
                    municipios={lubpressData.municipios}
                    transportadoras={lubpressData.transportadoras}
                  />
                )}
                {page === "lubOportunidades" && (
                  <LubOportunidades
                    ctes={lubpressData.ctes}
                    tabelasVibra={lubpressData.tabelasVibra}
                    tabelas={lubpressData.tabelasLub}
                    municipios={lubpressData.municipiosLub}
                    transportadoras={lubpressData.transportadoras}
                  />
                )}
                {page === "lubNotas" && (
                  <LubNotas
                    notas={lubpressData.notas}
                    salvarNotas={lubpressData.salvarNotas}
                    recarregar={lubpressData.recarregar}
                    ctes={lubpressData.ctes}
                    rotasVibra={lubpressData.rotasVibra}
                    municipios={lubpressData.municipiosLub}
                    transportadoras={lubpressData.transportadoras}
                    clientes={lubpressData.clientes}
                    setClientes={lubpressData.setClientes}
                    atualizarSelecaoNotas={lubpressData.atualizarSelecaoNotas}
                    usuarioAtual={nome}
                  />
                )}
                {(page === "lubOperacaoCWB" || page === "lubOperacaoRBP") && (
                  <LubOperacao
                    filial={page === "lubOperacaoCWB" ? "CWB" : "RBP"}
                    notas={lubpressData.notas}
                    produtos={lubpressData.produtos}
                    configPaletes={lubpressData.configPaletes}
                    paletizados={lubpressData.paletizados}
                    municipios={lubpressData.municipiosLub}
                    transportadoras={lubpressData.transportadoras}
                    clientes={lubpressData.clientes}
                    geracoes={lubpressData.geracoes}
                    salvarGeracao={lubpressData.salvarGeracao}
                    usuarioAtual={nome}
                  />
                )}
                {page === "lubProdutos" && (
                  <LubProdutos
                    produtos={lubpressData.produtos}
                    setProdutos={lubpressData.setProdutos}
                  />
                )}
                {page === "lubClientes" && (
                  <LubClientes
                    clientes={lubpressData.clientes}
                    setClientes={lubpressData.setClientes}
                  />
                )}
                {page === "lubCompMun" && (
                  <LubComparativoMun
                    ctes={lubpressData.ctes}
                    tabelasVibra={lubpressData.tabelasVibra}
                    tabelas={lubpressData.tabelas}
                    municipios={lubpressData.municipios}
                    transportadoras={lubpressData.transportadoras}
                  />
                )}
                {page === "lubMapa" && (
                  <LubMapa ctes={lubpressData.ctes}/>
                )}
                {page === "lubTransp" && (
                  <LubTransportadoras
                    transportadoras={lubpressData.transportadoras}
                    setTransportadoras={lubpressData.setTransportadoras}
                    podeEditarEmail={podeEditarEmailTransportadora(cargoUsuario)}
                  />
                )}
                {page === "lubMunicipios" && (
                  <LubMunicipios
                    municipios={lubpressData.municipiosLub}
                    setMunicipios={lubpressData.setMunicipiosLub}
                    transportadoras={lubpressData.transportadoras}
                  />
                )}
                {page === "lubRotasVibra" && (
                  <LubRotasVibra
                    rotasVibra={lubpressData.rotasVibra}
                    setRotasVibra={lubpressData.setRotasVibra}
                  />
                )}
                {page === "lubTabelas" && (
                  <LubTabelas
                    tabelas={lubpressData.tabelasLub}
                    setTabelas={lubpressData.setTabelas}
                    transportadoras={lubpressData.transportadoras}
                  />
                )}
                {page === "lubRetornoVibra" && (
                  <LubRetornoVibra
                    notas={lubpressData.notas}
                    produtos={lubpressData.produtos}
                    armazenagemNotas={lubpressData.armazenagemNotas}
                    salvarArmazenagemNotas={lubpressData.salvarArmazenagemNotas}
                    retornosRateio={lubpressData.retornosRateio}
                    salvarRetornosRateio={lubpressData.salvarRetornosRateio}
                  />
                )}
              </div>
            )}
            </Suspense>
          </main>

          {/* ══ FOOTER ════════════════════════════════════════════ */}
          <footer style={{
            height:34, flexShrink:0,
            background:T.header,
            borderTop:`1px solid ${T.border}`,
            display:"flex", alignItems:"center",
            justifyContent:"space-between",
            padding: estreito ? "0 12px" : "0 24px", gap:10,
            fontSize:11, color:T.muted, ...syne,
          }}>
            <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
              © 2025–2026 Gamma Distribuidora de Lubrificantes{estreito ? "" : " Ltda."}
            </span>
            <div title="Sistema operacional" style={{ display:"flex", alignItems:"center", gap:6, flexShrink:0 }}>
              <span style={{ width:6, height:6, borderRadius:"50%", background:T.green, display:"inline-block" }}/>
              {!estreito && <span>Sistema operacional</span>}
            </div>
          </footer>

        </div>{/* fim coluna direita */}
      </div>{/* fim layout */}
      {/* GammaBot — Assistente IA flutuante. É lazy: sem Suspense próprio,
          suspendia sem fronteira ao escolher o grupo (React #426, #171). */}
      <Suspense fallback={null}>
        <GammaBot
          notas={notas}
          tabelas={tabelas}
          ocorrencias={dadosFilial.ocorrencias}
          municipios={municipios}
          clientesMap={clientesMap}
          municipiosMap={municipiosMap}
        />
      </Suspense>
    </>
    </ErrorBoundary>
  );
}
